import { useCallback, useEffect, useMemo, useState } from "react";
import { getParsedStorageItem } from "common/src/utils/localStorageUtils";
import type { CustomView } from "../../../../datagrid/ColumnVisibilityModal";
import type { ColumnConfig, ColumnVisibility } from "../../../../datagrid-configs/types";
import type { GameRow } from "../../../../datagrid-configs/discoverGames";
import { DISCOVER_GAMES_DEFAULT_COLUMN_GROUP } from "../../../../datagrid-configs/discoverGames/constants";
import { mergeColumnOrder, mergeColumnVisibility } from "../helpers/orderColumnsForDisplay";

export interface UseDiscoverGamesColumnLayoutArgs {
	storageKey: string;
	customViewsKey: string;
	selectedViewKey: string;
	discoverGamesColumns: ColumnConfig<GameRow>[];
	initialColumnVisibility: ColumnVisibility;
}

export function useDiscoverGamesColumnLayout(args: UseDiscoverGamesColumnLayoutArgs) {
	const { storageKey, customViewsKey, selectedViewKey, discoverGamesColumns, initialColumnVisibility } = args;
	const columnWidthsStorageKey = `${storageKey}_columnWidths`;

	const defaultVisibility = useMemo(() => {
		const defaults: ColumnVisibility = {};
		discoverGamesColumns.forEach((col) => {
			defaults[String(col.key)] = col.defaultVisible !== false;
		});
		return { ...defaults, ...initialColumnVisibility };
	}, [discoverGamesColumns, initialColumnVisibility]);

	const defaultColumnOrder = useMemo(() => {
		return discoverGamesColumns.map((col) => String(col.key));
	}, [discoverGamesColumns]);

	const [visibleColumns, setVisibleColumns] = useState<ColumnVisibility>(() => {
		const views = getParsedStorageItem<CustomView[]>(customViewsKey, []);

		const storedViewId = localStorage.getItem(selectedViewKey);
		if (storedViewId) {
			const selectedView = views.find((v) => v.id === storedViewId);
			if (selectedView) {
				return mergeColumnVisibility(defaultVisibility, selectedView.visibility);
			}
		}

		const defaultView = views.find((v) => v.isDefault);
		if (defaultView) {
			return mergeColumnVisibility(defaultVisibility, defaultView.visibility);
		}

		return mergeColumnVisibility(
			defaultVisibility,
			getParsedStorageItem<ColumnVisibility>(storageKey, {}),
		);
	});

	const [columnOrder, setColumnOrder] = useState<string[]>(() =>
		mergeColumnOrder(
			discoverGamesColumns.map((col) => String(col.key)),
			getParsedStorageItem<string[]>(`${storageKey}_order`, []),
		),
	);

	const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
		getParsedStorageItem<Record<string, number>>(columnWidthsStorageKey, {}),
	);

	const [showColumnModal, setShowColumnModal] = useState(false);

	const handleColumnWidthsChange = useCallback((widths: Record<string, number>) => {
		setColumnWidths(widths);
	}, []);

	useEffect(() => {
		localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
	}, [visibleColumns, storageKey]);

	useEffect(() => {
		localStorage.setItem(`${storageKey}_order`, JSON.stringify(columnOrder));
	}, [columnOrder, storageKey]);

	useEffect(() => {
		localStorage.setItem(columnWidthsStorageKey, JSON.stringify(columnWidths));
	}, [columnWidths, columnWidthsStorageKey]);

	const applyVisibility = useCallback(
		(visibility: ColumnVisibility, order?: string[]) => {
			const mergedVisibility = mergeColumnVisibility(defaultVisibility, visibility);
			const visibleCount = Object.values(mergedVisibility).filter((v) => v !== false).length;
			let appliedVisibility = mergedVisibility;
			if (visibleCount < 1 && discoverGamesColumns.length > 0) {
				const firstKey = String(discoverGamesColumns[0].key);
				appliedVisibility = { ...mergedVisibility, [firstKey]: true };
			}
			setVisibleColumns(appliedVisibility);
			if (order) {
				setColumnOrder(mergeColumnOrder(defaultColumnOrder, order));
			}
		},
		[defaultColumnOrder, defaultVisibility, discoverGamesColumns],
	);

	const apiColumns = useMemo(() => {
		const fields = new Set<string>();

		discoverGamesColumns.forEach((col) => {
			const colKey = String(col.key);
			if (visibleColumns[colKey] !== false) {
				if (col.requiredFields) {
					col.requiredFields.forEach((field) => fields.add(field));
				} else {
					fields.add(colKey);
				}
			}
		});

		return Array.from(fields);
	}, [discoverGamesColumns, visibleColumns]);

	const columnGroups = useMemo(() => {
		const groups: Record<string, Array<{ key: string; label: string }>> = {};
		discoverGamesColumns.forEach((col) => {
			const group = col.group || DISCOVER_GAMES_DEFAULT_COLUMN_GROUP;
			if (!groups[group]) {
				groups[group] = [];
			}
			const key = String(col.key);
			groups[group].push({
				key,
				label: col.label,
			});
		});
		return groups;
	}, [discoverGamesColumns]);

	return {
		defaultVisibility,
		defaultColumnOrder,
		visibleColumns,
		columnOrder,
		columnWidths,
		showColumnModal,
		setShowColumnModal,
		handleColumnWidthsChange,
		applyVisibility,
		apiColumns,
		columnGroups,
	};
}
